import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Clock, User, ChevronRight } from 'lucide-react';
import articleContent from '../data/articleContent';

const authorBios = {
  'Marcus Chen, CIO': 'Chief Investment Officer with 20+ years in global macro and multi-asset portfolio management. Previously at Goldman Sachs and Bridgewater Associates.',
  'Victoria Ashworth, CEO': 'Chief Executive Officer and co-founder. Former Managing Director at BlackRock with expertise in alternative investments and institutional strategy.',
  'Amara Okafor, Head of Digital': 'Head of Digital Assets with deep expertise in blockchain infrastructure, tokenization, and institutional crypto adoption.',
  'James Harrington, CRO': 'Chief Risk Officer with two decades of experience in commodity markets, precious metals, and macro risk frameworks.',
  'Sofia Rodriguez, Head of Advisory': 'Head of Wealth Advisory with specialization in multi-generational planning, tax structuring, and family governance.',
  'David Nakamura, CTO': 'Chief Technology Officer leading our quantitative research and systematic trading platforms. PhD in Applied Mathematics from MIT.',
  'Research Team': 'Our global research team of 30+ analysts covers all 12 asset classes with rigorous, institutional-grade analysis.'
};

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = articleContent[parseInt(id)];

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, delay }
  });

  if (!article) {
    return (
      <div style={{ paddingTop: '12rem', textAlign: 'center', minHeight: '60vh' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Article Not Found</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>The article you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/insights')} style={{ background: 'var(--accent-gradient)', padding: '1rem 2rem', borderRadius: '12px', border: 'none', color: '#000', fontWeight: '700', cursor: 'pointer' }}>Back to Insights</button>
      </div>
    );
  }

  const relatedArticles = Object.entries(articleContent)
    .filter(([key]) => parseInt(key) !== parseInt(id))
    .slice(0, 3)
    .map(([key, a]) => ({ id: key, ...a }));

  return (
    <div style={{ paddingTop: '5rem', color: 'var(--text-primary)' }}>
      {/* Hero Image Banner */}
      <div style={{ position: 'relative', height: '400px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${article.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.8))' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', margin: '0 auto', padding: '0 5%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: '3rem' }}>
          <motion.div {...fadeUp()}>
            <button onClick={() => navigate('/insights')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', marginBottom: '1.5rem', padding: 0 }}>
              <ArrowLeft size={16} /> Back to Insights
            </button>
            <span style={{ background: 'var(--accent-primary)', color: '#000', padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-block', marginBottom: '1rem' }}>{article.category}</span>
            <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', color: '#fff', lineHeight: '1.2', marginBottom: '1.5rem' }}>{article.title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><User size={15} /> {article.author}</span>
              <span>{article.date}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Clock size={15} /> {article.readTime} read</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Article Body */}
      <article style={{ maxWidth: '740px', margin: '0 auto', padding: '3rem 5% 4rem' }}>
        {article.body.map((block, i) => {
          if (block.type === 'paragraph') {
            return (
              <motion.p key={i} {...fadeUp(i * 0.03)} style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: '1.9', marginBottom: '1.5rem' }}>
                {block.text}
              </motion.p>
            );
          }
          if (block.type === 'heading') {
            return (
              <motion.h2 key={i} {...fadeUp(i * 0.03)} style={{ fontSize: '1.6rem', fontWeight: '800', marginTop: '2.5rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                {block.text}
              </motion.h2>
            );
          }
          if (block.type === 'pullQuote') {
            return (
              <motion.blockquote key={i} {...fadeUp(i * 0.03)} style={{
                borderLeft: '4px solid var(--accent-primary)', margin: '2.5rem 0', padding: '1.5rem 2rem',
                background: 'var(--bg-secondary)', borderRadius: '0 16px 16px 0',
                fontSize: '1.2rem', fontWeight: '600', fontStyle: 'italic', lineHeight: '1.6', color: 'var(--text-primary)'
              }}>
                "{block.text}"
              </motion.blockquote>
            );
          }
          if (block.type === 'keyTakeaways') {
            return (
              <motion.div key={i} {...fadeUp(i * 0.03)} style={{
                background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '20px',
                padding: '2rem', margin: '2.5rem 0'
              }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  📌 Key Takeaways
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {block.items.map((item, j) => (
                    <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', fontSize: '0.95rem', lineHeight: '1.5', color: 'var(--text-secondary)' }}>
                      <ChevronRight size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.2rem' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          }
          return null;
        })}

        {/* Author Bio */}
        <motion.div {...fadeUp()} style={{
          marginTop: '3rem', padding: '2rem', borderRadius: '20px',
          background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
          display: 'flex', gap: '1.5rem', alignItems: 'center'
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0,
            background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.5rem', fontWeight: '800', color: '#000'
          }}>
            {article.author.split(' ').map(n => n[0]).slice(0, 2).join('')}
          </div>
          <div>
            <h4 style={{ fontWeight: '700', marginBottom: '0.3rem' }}>{article.author}</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0, lineHeight: '1.5' }}>
              {authorBios[article.author] || 'Senior member of the Quantis Capital research and investment team.'}
            </p>
          </div>
        </motion.div>
      </article>

      {/* Related Articles */}
      <section style={{ padding: '4rem 5% 6rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '2rem' }}>Related Research</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {relatedArticles.map((a, i) => (
              <motion.div key={a.id} {...fadeUp(i * 0.08)}
                onClick={() => { navigate(`/insights/${a.id}`); window.scrollTo(0, 0); }}
                style={{
                  background: 'var(--bg-primary)', borderRadius: '20px', overflow: 'hidden',
                  border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.3s'
                }}
                whileHover={{ y: -4, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
              >
                <div style={{ height: '160px', backgroundImage: `url(${a.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ padding: '1.5rem' }}>
                  <span style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '600', border: '1px solid var(--border-color)' }}>{a.category}</span>
                  <h3 style={{ fontSize: '1rem', fontWeight: '800', margin: '0.7rem 0 0.5rem', lineHeight: '1.3' }}>{a.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{a.date} · {a.readTime}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetail;
